import React from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Button,
  Menu,
} from '@mui/material';
import { INK, FONTS, ghostBtnSx } from '../../styles/ledger';

/* ─────────────────────────────────────────────
   PAGE HEADER — eyebrow + serif title + actions
   ───────────────────────────────────────────── */
export const PageHeader: React.FC<{
  eyebrow: string;
  title: React.ReactNode;
  description?: string;
  actions?: React.ReactNode;
}> = ({ eyebrow, title, description, actions }) => (
  <Box
    sx={{
      mb: { xs: 4, md: 5 },
      animation: 'fadeUp 0.5s ease-out',
      '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
      <Box sx={{ width: 24, height: 1, bgcolor: INK.accent }} />
      <Typography
        sx={{
          fontFamily: FONTS.mono,
          fontSize: 11,
          letterSpacing: '0.18em',
          color: INK.accent,
          fontWeight: 500,
          textTransform: 'uppercase',
        }}
      >
        {eyebrow}
      </Typography>
    </Stack>
    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'flex-end' }} justifyContent="space-between" spacing={3}>
      <Box sx={{ maxWidth: 720, minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: FONTS.serif,
            fontSize: { xs: 40, md: 56 },
            lineHeight: 0.96,
            letterSpacing: '-0.02em',
            color: INK.text,
            mb: description ? 2 : 0,
            fontWeight: 400,
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography sx={{ fontFamily: FONTS.sans, fontSize: 14.5, lineHeight: 1.55, color: INK.muted, maxWidth: 560 }}>
            {description}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
          {actions}
        </Stack>
      )}
    </Stack>
  </Box>
);

/* ─────────────────────────────────────────────
   KPI STRIP + cell
   ───────────────────────────────────────────── */
export const KPIStrip: React.FC<{ children: React.ReactNode; cols?: number }> = ({ children, cols }) => {
  const count = cols ?? React.Children.count(children);
  const mdTemplate = `repeat(${count}, 1fr)`;
  return (
    <Box
      sx={{
        border: `1px solid ${INK.hairline}`,
        bgcolor: INK.surface,
        borderRadius: 0.5,
        mb: 4,
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: mdTemplate },
        animation: 'fadeUp 0.6s ease-out',
      }}
    >
      {React.Children.map(children, (child, i) => {
        if (!React.isValidElement(child)) return child;
        return React.cloneElement(child as any, {
          isLast: i === count - 1,
          total: count,
          index: i,
        });
      })}
    </Box>
  );
};

export const KPI: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent?: boolean;
  // injected by KPIStrip
  isLast?: boolean;
  total?: number;
  index?: number;
}> = ({ label, value, sub, accent, isLast, total, index }) => {
  // Compute row position for mobile 2-col grid: bottom-border off for last row
  const mobileLastRow = total !== undefined && index !== undefined && index >= total - 2;
  return (
    <Box
      sx={{
        px: { xs: 2.25, md: 3.5 },
        py: { xs: 2.5, md: 3.5 },
        borderRight: { md: isLast ? 'none' : `1px solid ${INK.hairline}` },
        borderBottom: { xs: mobileLastRow ? 'none' : `1px solid ${INK.hairline}`, md: 'none' },
      }}
    >
      <Typography
        sx={{
          fontFamily: FONTS.mono,
          fontSize: 10,
          letterSpacing: '0.16em',
          color: INK.subtle,
          textTransform: 'uppercase',
          mb: 1.25,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONTS.serif,
          fontSize: { xs: 32, md: 44 },
          lineHeight: 0.95,
          color: accent ? INK.accent : INK.text,
          letterSpacing: '-0.02em',
          mb: sub ? 0.75 : 0,
          minHeight: { xs: 32, md: 44 },
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontFamily: FONTS.sans, fontSize: 11.5, color: INK.subtle }}>{sub}</Typography>
      )}
    </Box>
  );
};

/* ─────────────────────────────────────────────
   FILTER BAR — always visible, hairline framed
   ───────────────────────────────────────────── */
export const FilterBar: React.FC<{ children: React.ReactNode; label?: string }> = ({ children, label = 'Filters' }) => (
  <Box
    sx={{
      border: `1px solid ${INK.hairline}`,
      bgcolor: INK.surface,
      borderRadius: 0.5,
      p: 2.5,
      mb: 3,
      animation: 'fadeUp 0.7s ease-out',
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
      <Typography
        sx={{
          fontFamily: FONTS.mono,
          fontSize: 10,
          letterSpacing: '0.18em',
          color: INK.accent,
          fontWeight: 500,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: 1, bgcolor: INK.hairlineSoft }} />
    </Stack>
    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ lg: 'center' }}>
      {children}
    </Stack>
  </Box>
);

/* ─────────────────────────────────────────────
   LEDGER CARD — wrapping container for sections
   ───────────────────────────────────────────── */
export const LedgerCard: React.FC<{
  children: React.ReactNode;
  padded?: boolean;
  noBorder?: boolean;
  mb?: number;
}> = ({ children, padded = true, noBorder = false, mb = 0 }) => (
  <Box
    sx={{
      border: noBorder ? 'none' : `1px solid ${INK.hairline}`,
      bgcolor: INK.surface,
      borderRadius: 0.5,
      p: padded ? 3 : 0,
      mb,
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
);

/* ─────────────────────────────────────────────
   SECTION HEADER (§ NN — eyebrow — title)
   ───────────────────────────────────────────── */
export const SectionHeader: React.FC<{
  number?: string;
  eyebrow: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}> = ({ number, eyebrow, title, description, action }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 2.5 }}>
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mb: 0.5 }}>
        {number && (
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.accent, fontWeight: 600, letterSpacing: '0.04em' }}>
            § {number}
          </Typography>
        )}
        <Typography
          sx={{
            fontFamily: FONTS.mono,
            fontSize: 10,
            letterSpacing: '0.18em',
            color: INK.accent,
            textTransform: 'uppercase',
            fontWeight: 500,
          }}
        >
          {eyebrow}
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontFamily: FONTS.serif,
          fontSize: 22,
          letterSpacing: '-0.01em',
          color: INK.text,
          fontWeight: 400,
          lineHeight: 1.15,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography sx={{ fontFamily: FONTS.sans, fontSize: 12.5, color: INK.muted, mt: 0.5 }}>
          {description}
        </Typography>
      )}
    </Box>
    {action}
  </Stack>
);

/* ─────────────────────────────────────────────
   ACTION ICON — small icon button
   ───────────────────────────────────────────── */
export const ActionIcon: React.FC<{
  title: string;
  onClick: (e?: any) => void;
  children: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
}> = ({ title, onClick, children, danger, disabled }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
          width: 28,
          height: 28,
          borderRadius: 0.5,
          color: INK.muted,
          '&:hover': {
            bgcolor: danger ? INK.inactiveSoft : INK.accentSoft,
            color: danger ? INK.inactive : INK.accent,
          },
          '&.Mui-disabled': { color: INK.faint },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

/* ─────────────────────────────────────────────
   STATUS DOT — colored dot + uppercase mono label
   ───────────────────────────────────────────── */
export const StatusDot: React.FC<{
  label: string;
  tone?: 'active' | 'inactive' | 'warning' | 'neutral' | 'info';
}> = ({ label, tone = 'neutral' }) => {
  const color =
    tone === 'active' ? INK.active
      : tone === 'inactive' ? INK.inactive
      : tone === 'warning' ? INK.warning
      : tone === 'info' ? '#2A5F8C'
      : INK.muted;
  const soft =
    tone === 'active' ? INK.accentSoft
      : tone === 'inactive' ? INK.inactiveSoft
      : tone === 'warning' ? INK.amberSoft
      : tone === 'info' ? '#EAF1F8'
      : INK.hairlineSoft;
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: color,
          boxShadow: tone !== 'neutral' ? `0 0 0 3px ${soft}` : 'none',
        }}
      />
      <Typography
        sx={{
          fontFamily: FONTS.mono,
          fontSize: 10.5,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: color,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
};

/* ─────────────────────────────────────────────
   MONO TAG — mono-font bordered label
   ───────────────────────────────────────────── */
export const MonoTag: React.FC<{ children: React.ReactNode; accent?: boolean }> = ({ children, accent }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      fontFamily: FONTS.mono,
      fontSize: 10.5,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: accent ? INK.accent : INK.text,
      border: `1px solid ${accent ? INK.accent : INK.hairline}`,
      bgcolor: accent ? INK.accentSoft : INK.surfaceTint,
      px: 0.85,
      py: 0.3,
      fontWeight: 600,
    }}
  >
    {children}
  </Box>
);

/* ─────────────────────────────────────────────
   EMPTY STATE — for empty tables
   ───────────────────────────────────────────── */
export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  description?: string;
}> = ({ icon, title, description }) => (
  <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center', py: 6 }}>
    {icon && (
      <Box
        sx={{
          width: 56,
          height: 56,
          border: `1px solid ${INK.hairline}`,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: INK.subtle,
        }}
      >
        {icon}
      </Box>
    )}
    <Typography sx={{ fontFamily: FONTS.serif, fontSize: 26, color: INK.text, letterSpacing: '-0.01em' }}>
      {title}
    </Typography>
    {description && (
      <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.muted, maxWidth: 360 }}>
        {description}
      </Typography>
    )}
  </Stack>
);

/* ─────────────────────────────────────────────
   PAGE WRAPPER — cream paper background
   ───────────────────────────────────────────── */
export const LedgerPage: React.FC<{ children: React.ReactNode; eyebrowFooter?: string }> = ({ children, eyebrowFooter }) => (
  <Box
    sx={{
      fontFamily: FONTS.sans,
      bgcolor: INK.bg,
      color: INK.text,
      mx: { xs: -2, md: -3 },
      my: { xs: -2, md: -3 },
      px: { xs: 3, md: 6 },
      py: { xs: 4, md: 5 },
      minHeight: 'calc(100vh - 64px)',
      backgroundImage:
        'radial-gradient(ellipse at top left, rgba(31,78,61,0.025), transparent 50%), radial-gradient(ellipse at bottom right, rgba(184,101,27,0.02), transparent 50%)',
    }}
  >
    {children}
    {eyebrowFooter && (
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 5, opacity: 0.6 }}>
        <Typography
          sx={{
            fontFamily: FONTS.mono,
            fontSize: 10,
            letterSpacing: '0.18em',
            color: INK.subtle,
            textTransform: 'uppercase',
          }}
        >
          {eyebrowFooter}
        </Typography>
        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, color: INK.subtle }}>
          {new Date().getFullYear()}
        </Typography>
      </Stack>
    )}
  </Box>
);

/* ─────────────────────────────────────────────
   TABLE HEADER cell sx — apply to TableHead rows
   ───────────────────────────────────────────── */
export const ledgerTableHeadSx = {
  '& .MuiTableCell-head': {
    fontFamily: FONTS.mono,
    fontSize: 10,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color: INK.subtle,
    fontWeight: 500,
    borderBottom: `1px solid ${INK.hairline}`,
    bgcolor: INK.surfaceTint,
    py: 1.5,
  },
};

export const ledgerRowSx = {
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${INK.hairlineSoft}`,
    fontFamily: FONTS.sans,
    py: 2,
    verticalAlign: 'top' as const,
  },
  position: 'relative',
  transition: 'background-color 0.15s ease',
  '&:hover': { bgcolor: INK.surfaceTint },
  '&:hover .row-actions': { opacity: 1 },
};

export const ledgerPaginationSx = {
  borderTop: `1px solid ${INK.hairline}`,
  fontFamily: FONTS.sans,
  color: INK.muted,
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-input': {
    fontFamily: FONTS.mono,
    fontSize: 11.5,
    letterSpacing: '0.04em',
    color: INK.muted,
  },
  '& .MuiTablePagination-toolbar': { paddingLeft: 3, paddingRight: 3, minHeight: 48 },
  '& .MuiIconButton-root': { color: INK.muted, '&:hover': { color: INK.accent, bgcolor: INK.accentSoft } },
};

/* Menu paper sx for ledger-style dropdowns */
export const ledgerMenuPaperSx = {
  borderRadius: 0.5,
  border: `1px solid ${INK.hairline}`,
  boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
  mt: 0.5,
  '& .MuiMenuItem-root': {
    fontFamily: FONTS.sans,
    fontSize: 13,
    py: 1.1,
    color: INK.text,
    '&:hover': { bgcolor: INK.surfaceTint },
  },
  '& .MuiListItemIcon-root': { color: INK.muted, minWidth: 32 },
  '& .MuiDivider-root': { borderColor: INK.hairlineSoft },
};

/* IconBtn helper for header toolbar — bordered hairline */
export const HeaderIconBtn: React.FC<{
  title: string;
  onClick: () => void;
  badge?: number;
  children: React.ReactNode;
}> = ({ title, onClick, badge, children }) => (
  <Tooltip title={title}>
    <IconButton
      onClick={onClick}
      sx={{
        border: `1px solid ${INK.hairline}`,
        borderRadius: 0.5,
        width: 40,
        height: 40,
        color: INK.muted,
        position: 'relative',
        '&:hover': { bgcolor: INK.surfaceTint, borderColor: INK.text, color: INK.text },
      }}
      size="small"
    >
      {children}
      {badge !== undefined && badge > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            minWidth: 16,
            height: 16,
            bgcolor: INK.inactive,
            color: INK.surface,
            borderRadius: 1,
            fontFamily: FONTS.mono,
            fontSize: 9,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 0.4,
            border: `1px solid ${INK.bg}`,
          }}
        >
          {badge > 99 ? '99+' : badge}
        </Box>
      )}
    </IconButton>
  </Tooltip>
);

/* Compact Menu wrapper with ledger paper styles */
export const LedgerMenu: React.FC<{
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
}> = ({ anchorEl, open, onClose, children, width = 200 }) => (
  <Menu
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    PaperProps={{ sx: { ...ledgerMenuPaperSx, minWidth: width } }}
  >
    {children}
  </Menu>
);

/* Date-range pill button with menu trigger */
export const DateRangePill: React.FC<{ label: string; onClick: (e: React.MouseEvent<HTMLElement>) => void; icon?: React.ReactNode }> = ({
  label,
  onClick,
  icon,
}) => (
  <Button onClick={onClick} sx={ghostBtnSx} startIcon={icon}>
    {label}
  </Button>
);
